import {
  AllocationRuleDto,
  CarrierVisitDirection,
  CarrierVisitResponse,
  CarrierVisitStatus,
  JourneyResponse,
} from '@yard/app/api'
import { DATE_RANGE_OPTIONS, DateRangeEnum } from '@yard/constants/dateRanges'
import { AllocationRuleStore } from '@yard/stores/allocation-rule.store'
import { CarrierVisitStore } from '@yard/stores/carrier-visit.store'
import { JourneyStore } from '@yard/stores/journey.store'
import { filterCarrierVisits, getVisitJourneys } from '@yard/utils/carrier-visit.util'
import { aggregateJourneysByRules } from '@yard/utils/journey.util'
import dayjs, { Dayjs } from 'dayjs'
import { action, makeObservable, observable } from 'mobx'
import { CarrierType } from 'modules/utils/src/components/ContainerJourneyCard/CarrierTypeIcon'

type CarrierVisitOverviewIndicatorsData = {
  direction: CarrierVisitDirection
  containerCount: number
  globalRuleMatchCount: number
  visitRuleMatchCount: number
  unallocatedCount: number
}

export type CarrierVisitOverviewData = CarrierVisitResponse & {
  visitTimestamp: string
  indicators: CarrierVisitOverviewIndicatorsData[]
}
export class CarrierVisitOverviewViewStore {
  isOverviewOpen = false

  selectedDate?: Dayjs = dayjs().add(30, 'day')
  selectedTypes?: CarrierType[]
  selectedStatus?: CarrierVisitStatus[]

  constructor(
    readonly carrierVisitStore: CarrierVisitStore,
    readonly journeyStore: JourneyStore,
    readonly allocationRuleStore: AllocationRuleStore,
  ) {
    makeObservable(this, {
      isOverviewOpen: observable,
      selectedDate: observable,
      selectedTypes: observable,
      selectedStatus: observable,

      setOverviewOpen: action,
      setSelectedDateFilter: action,
      setSelectedDate: action,
      setSelectedTypes: action,
      setSelectedStatus: action,

      resetOverviewFilters: action,
    })
  }

  get carrierVisitsGroupedByDate() {
    const visits = this.carrierVisitStore?.items || []
    const journeys = this.journeyStore.items

    const globalRules = this.allocationRuleStore.globalRules
    const visitRules = this.allocationRuleStore.carrierVisitRules

    const selectedDate = this.selectedDate
    const selectedTypes = this.selectedTypes
    const selectedStatus = this.selectedStatus

    const groupedVisits: Record<string, CarrierVisitOverviewData[]> = {}
    const filteredVisits = filterCarrierVisits(visits, selectedDate, selectedTypes, selectedStatus)

    filteredVisits.forEach(visit => {
      const date = dayjs(visit.eta || visit.ata)
      const dateKey = date.format('ddd DD/MM')
      const dateTime = date.format('HH:mm, ddd DD/MM')

      if (!groupedVisits[dateKey]) groupedVisits[dateKey] = []

      const mappedCarrierVisit: CarrierVisitOverviewData = {
        ...visit,
        visitTimestamp: dateTime,
        indicators: [
          buildIndicator(journeys, visitRules, globalRules, visit, CarrierVisitDirection.Inbound),
          buildIndicator(journeys, visitRules, globalRules, visit, CarrierVisitDirection.Outbound),
        ],
      }

      groupedVisits[dateKey].push(mappedCarrierVisit)
    })

    return groupedVisits
  }

  setOverviewOpen(isOpen: boolean) {
    this.isOverviewOpen = isOpen
  }

  setSelectedDateFilter(key?: DateRangeEnum) {
    if (!key) {
      this.setSelectedDate()
      return
    }

    const found = DATE_RANGE_OPTIONS.find(opt => opt.key === key)
    if (found) {
      this.setSelectedDate(dayjs().add(found.days, 'day'))
    }
  }

  setSelectedDate(date?: Dayjs) {
    this.selectedDate = date
  }

  setSelectedTypes(types?: CarrierType[]) {
    this.selectedTypes = types
  }

  setSelectedStatus(status?: CarrierVisitStatus[]) {
    this.selectedStatus = status
  }

  resetOverviewFilters() {
    this.selectedDate = dayjs().add(30, 'day')
    this.selectedTypes = [CarrierType.Vessel]
    this.selectedStatus = [CarrierVisitStatus.Expected]
  }
}

export function buildIndicator(
  allJourneys: JourneyResponse[],
  visitRules: AllocationRuleDto[],
  globalRules: AllocationRuleDto[],
  visit: CarrierVisitResponse,
  direction: CarrierVisitDirection,
): CarrierVisitOverviewIndicatorsData {
  const filteredJourneys = getVisitJourneys(allJourneys, visit, direction)

  // Narrow the rule scope to what the allocation-rules drawer for this visit+direction
  // sees: only this visit's CV rules (direction-exact) plus all global rules. This keeps
  // a journey that is cross-linked to another visit from being bucketed into that other
  // visit's rule, so the indicator's numbers agree with the drawer.
  const thisVisitRules = visitRules.filter(
    r => r.facets?.carrierVisitId === visit.id && r.facets?.direction === direction,
  )
  const scopedRules = [...thisVisitRules, ...globalRules]
  const aggregated = aggregateJourneysByRules(filteredJourneys, scopedRules)

  const thisVisitRuleIds = new Set(thisVisitRules.map(r => r.id))
  const globalRuleIds = new Set(globalRules.map(r => r.id))

  let visitRuleMatchCount = 0
  let globalRuleMatchCount = 0

  Object.entries(aggregated).forEach(([ruleId, journeysInBucket]) => {
    if (ruleId === 'unassigned') return
    if (thisVisitRuleIds.has(ruleId)) visitRuleMatchCount += journeysInBucket.length
    else if (globalRuleIds.has(ruleId)) globalRuleMatchCount += journeysInBucket.length
  })

  return {
    direction,
    containerCount: filteredJourneys.length,
    globalRuleMatchCount,
    visitRuleMatchCount,
    unallocatedCount: filteredJourneys.length - visitRuleMatchCount - globalRuleMatchCount,
  }
}
